import _ from 'lodash';
import AppDispatcher from '../dispatcher/AppDispatcher';
import { ActionTypes, ReservationBundleOutcome, ReservationContexts, ReservationErrorCodes } from '../constants/AppConstants';
import ReservationsService from '../services/ReservationsService';
import ReservationRequestStore from '../stores/ReservationRequestStore';
import { buildSalesReservationPayload, getReservationRequestJourney } from '../utils/reservationUtils';



const ReservationActions = {
    setIsReservationStarted() {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.START_RESERVATION_FLOW,
            }),
        );
    },

    setIsReservationCanceled() {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.CANCEL_RESERVATION_FLOW,
            }),
        );
    },

    setIsReservationCompleted() {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.RESERVATION_COMPLETED,
            }),
        );
    },

    addBikeReservation(bikeReservation) {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.ADD_BIKE_RESERVATION,
                bikeReservation,
            }),
        );
    },

    removeBikeReservation() {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.REMOVE_BIKE_RESERVATION,
            }),
        );
    },

    addSeatReservation(seatReservation) {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.ADD_SEAT_RESERVATION,
                seatReservation,
            }),
        );
    },

    removeSeatReservation() {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.REMOVE_SEAT_RESERVATION,
            }),
        );
    },

    setReservationWidgetToggled(toggled) {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.RESERVATION_WIDGET_TOGGLED,
                toggled,
            }),
        );

        if (toggled) {
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.START_RESERVATION_FLOW,
                    toggled,
                }),
            );
        } else {
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.CANCEL_RESERVATION_FLOW,
                    toggled,
                }),
            );
        }
    },

    clearReservationStore() {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.CLEAR_RESERVATION_STORE,
            }),
        );
    },

    /*
        Check the availability of reservations for the current reservation request
        or for the provided options.
    */
    async checkAvailabilityForReservation(opts) {
        const reservationRequestStoreState = ReservationRequestStore.getState();

        let outwardService = null;
        let inwardService = null;
        let outwardFare = null;
        let inwardFare = null;

        let bikesCount = 0;
        let passengers = null;
        let requestSeats = false;

        const isBookingFlow = opts && opts.context === ReservationContexts.TICKET_BOOKING;
        const isBasicAvailabilityCheck = opts && opts.isBasicAvailabilityCheck;

        passengers = opts.passengers || reservationRequestStoreState.reservationRequest.passengers;

        if (isBookingFlow) {
            bikesCount = opts.bikesCount;
            requestSeats = opts.requestSeats;

            outwardService = opts.outwardService;
            inwardService = opts.inwardService;
            outwardFare = opts.outwardFare;
            inwardFare = opts.inwardFare;
        } else {
            const { reservationRequest } = reservationRequestStoreState;
            bikesCount = isBasicAvailabilityCheck ? 1 : reservationRequest.get('bikes');

            requestSeats = isBasicAvailabilityCheck ? true : reservationRequest.get('requestSeats');

            outwardService = reservationRequestStoreState.outwardService;
            inwardService = reservationRequestStoreState.inwardService;
            outwardFare = reservationRequestStoreState.outwardFare;
            inwardFare = reservationRequestStoreState.inwardFare;
        }

        const availabilityParams = buildSalesReservationPayload({
            outwardService,
            inwardService,
            outwardFare,
            inwardFare,
            passengers,
            bikesCount,
            requestSeats,
        });

        const availabilityResponse = await ReservationActions.getReservationsAvailability(availabilityParams);

        // Determine which bundles are required
        const requiredBundles = [];

        if (!passengers || passengers.size < 1) {
            return false;
        }

        if (requestSeats) {
            requiredBundles.push('SEATS_OUTBOUND');
            if (inwardService && inwardFare) {
                requiredBundles.push('SEATS_INBOUND');
            }
        }

        if (bikesCount > 0) {
            requiredBundles.push('BIKES_OUTBOUND');
            if (inwardService && inwardFare) {
                requiredBundles.push('BIKES_INBOUND');
            }
        }

        // Create a lookup for bundle outcomes
        const bundleAvailabilityMap = {};

        if (availabilityResponse && availabilityResponse.journeys && Array.isArray(availabilityResponse.journeys)) {
            for (var i = 0; i < availabilityResponse.journeys.length; i++) {
                var journey = availabilityResponse.journeys[i];
                if (journey.bundles && Array.isArray(journey.bundles)) {
                    for (var j = 0; j < journey.bundles.length; j++) {
                        var bundle = journey.bundles[j];
                        bundleAvailabilityMap[bundle.id] = bundle.bundleOutcome;
                    }
                }
            }
        }

        // Check outbound and inbound availability separately using requiredBundles as source of truth
        var hasFullOutboundAvailability = true;
        var hasFullInboundAvailability = true;

        for (var k = 0; k < requiredBundles.length; k++) {
            var bundleId = requiredBundles[k];
            if (bundleId.indexOf('OUTBOUND') !== -1) {
                if (bundleAvailabilityMap[bundleId] !== ReservationBundleOutcome.FULL_AVAILABILITY) {
                    hasFullOutboundAvailability = false;
                }
            }
            if (bundleId.indexOf('INBOUND') !== -1) {
                if (bundleAvailabilityMap[bundleId] !== ReservationBundleOutcome.FULL_AVAILABILITY) {
                    hasFullInboundAvailability = false;
                }
            }
        }

        return {
            bundleAvailabilityMap,
            requiredBundles,
            hasFullOutboundAvailability,
            hasFullInboundAvailability,
            hasFullAvailability: hasFullOutboundAvailability && hasFullInboundAvailability
        };
    },

    /*
        Create a standalone reservation based on the current reservation request state.

        This is not used by the bike reservation widget, but is used by the standalone booking flow
        to create a reservation based on the current reservation request state.

        The Booking flow just adds seats/bikes to the trip/order.
    */
    async createReservation() {
        AppDispatcher.dispatch({
            actionType: ActionTypes.CREATE_RESERVATION_STARTED,
        });

        try {
            let reservation = null;

            const reservationRequestStoreState = ReservationRequestStore.getState();

            const { reservationRequest } = reservationRequestStoreState;

            const outwardService = reservationRequestStoreState.outwardService;
            const inwardService = reservationRequestStoreState.inwardService;
            const outwardFare = reservationRequestStoreState.outwardFare;
            const inwardFare = reservationRequestStoreState.inwardFare;

            let reservationRequestWithJourneys = reservationRequest.set(
                'outwardJourney',
                getReservationRequestJourney(outwardFare, outwardService)
            );

            if (inwardService && inwardFare) {
                reservationRequestWithJourneys = reservationRequestWithJourneys.set(
                    'returnJourney',
                    getReservationRequestJourney(inwardFare, inwardService)
                );
            }

            const {
                hasFullAvailability
            } = await ReservationActions.checkAvailabilityForReservation({
                    context: ReservationContexts.STANDALONE_BOOKING,
                    isBasicAvailabilityCheck: false, // We want to check full availability when actually trying to make a reservation
                });

            if (hasFullAvailability) {
                reservation = await ReservationsService.createReservation(reservationRequestWithJourneys);

                AppDispatcher.dispatch({
                    actionType: ActionTypes.CREATE_RESERVATION_LOADED,
                    reservation,
                });

                return {
                    error: null,
                    reservation,
                };
            } else {
                AppDispatcher.dispatch({
                    actionType: ActionTypes.CREATE_RESERVATION_FAILED,
                    err: 'Unfortunately, there are no reservations available for these service(s)',
                });
                return {
                    err: 'Unfortunately, there are no reservations available for these service(s)',
                    reservation: null,
                };

            }
        } catch (err) {
            if (err.body && err.body.errors && err.body.errors.errorType === ReservationErrorCodes.RESERVATION_LIMIT_REACHED) {
                const error = new Error('You’ve reached the maximum number of reservations allowed for this week. Please check back next week or contact support if you need further assistance.');
                error.code = ReservationErrorCodes.RESERVATION_LIMIT_REACHED;
                AppDispatcher.dispatch({
                    actionType: ActionTypes.CREATE_RESERVATION_FAILED,
                    err: error,
                });
                return {
                    err: error,
                    reservation: null,
                };
            } else {
                AppDispatcher.dispatch({
                    actionType: ActionTypes.CREATE_RESERVATION_FAILED,
                    err,
                });
                return {
                    err,
                    reservation: null,
                };
            }
        } finally {
            AppDispatcher.dispatch({
                actionType: ActionTypes.CREATE_RESERVATION_FINISHED,
            });
            AppDispatcher.dispatch({
                actionType: ActionTypes.RESET_RESERVATION_REQUEST_OUTBOUND_SERVICE,
            });
            AppDispatcher.dispatch({
                actionType: ActionTypes.RESET_RESERVATION_REQUEST_INBOUND_SERVICE,
            });
            AppDispatcher.dispatch({
                actionType: ActionTypes.RESET_RESERVATION_REQUEST_OUTBOUND_FARE,
            });
            AppDispatcher.dispatch({
                actionType: ActionTypes.RESET_RESERVATION_REQUEST_INBOUND_FARE,
            });
        }
    },

    async amendReservation(reservation) {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.AMEND_RESERVATION_STARTED,
            }),
        );
        try {
            const { result: amendedReservation } = await ReservationsService.amendReservation(
                reservation,
            );
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.AMEND_RESERVATION_LOADED,
                    amendedReservation,
                }),
            );
            return reservation;
        } catch (err) {
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.AMEND_RESERVATION_FAILED,
                    err,
                }),
            );
            return null;
        } finally {
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.AMEND_RESERVATION_FINISHED,
                }),
            );
        }
    },

    async getReservations(page = 1) {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.GET_RESERVATION_STARTED,
            }),
        );
        try {
            const { reservations, pagination } = await ReservationsService.getReservations(page);
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.GET_RESERVATION_LOADED,
                    reservations,
                    pagination
                }),
            );
            return reservations;
        } catch (err) {
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.GET_RESERVATION_FAILED,
                    err,
                }),
            );
            return null;
        } finally {
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.GET_RESERVATION_FINISHED,
                }),
            );
        }
    },

    async cancelReservation(reservationId) {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.CANCEL_RESERVATION_STARTED,
            }),
        );
        try {
            const reservation = await ReservationsService.cancelReservation(
                reservationId,
            );
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.CANCEL_RESERVATION_SUCCESS,
                }),
            );

            return reservation;
        } catch (err) {
            console.warn('Error canceling reservation:', err);
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.CANCEL_RESERVATION_FAILED,
                    err,
                }),
            );
            return null;
        } finally {
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.CANCEL_RESERVATION_FINISHED,
                }),
            );
        }
    },

    async getReservationsAvailability({ journeys, passengers }) {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.GET_RESERVATIONS_AVAILABILITY_STARTED,
            }),
        );
        try {
            const response = await ReservationsService.getReservationsAvailability({
                journeys,
                passengers,
            });
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.GET_RESERVATIONS_AVAILABILITY_SUCCESS,
                }),
            );
            return response;
        } catch (err) {
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.GET_RESERVATIONS_AVAILABILITY_FAILED,
                    err,
                }),
            );
            return null;
        } finally {
            _.defer(() =>
                AppDispatcher.dispatch({
                    actionType: ActionTypes.GET_RESERVATIONS_AVAILABILITY_FINISHED,
                }),
            );
        }
    }
};

export default ReservationActions;
